'use client';

import { CmsEditorialIcon } from '@/cms/components/blocks/shared/cms-editorial-icon';
import { CmsFooterLocaleSwitcher } from '@/cms/components/chrome/cms-footer-locale-switcher';
import type { CmsFooterLocaleSwitcherModel } from '@/cms/components/chrome/cms-footer-locale-switcher.types';
import {
  type CmsSiteChromeEditTargetAttributes,
  type CmsSiteChromeTarget,
  CmsSiteChromeTargetTrigger,
} from '@/cms/components/chrome/cms-site-chrome-target';
import {
  AppIconByName,
  renderAppIcon,
} from '@/components/icons/app-icon-registry';
import { DiscordIcon } from '@/components/icons/social/networks/discord';
import { FooterExternalBadges } from '@/components/layout/system/footer-external-badges';
import { Logo } from '@/components/layout/system/logo';
import { CmsNavLinkBadge } from './cms-nav-link-badge';
import footerStyles from './cms-site-footer.module.css';

export type CmsSiteFooterIcon =
  | {
      kind: 'app';
      name: string;
    }
  | {
      kind: 'discord';
    }
  | {
      kind: 'editorial';
      name: string;
    }
  | {
      kind: 'email';
    };

export type CmsSiteFooterLink = {
  badge?: string;
  label: string;
  target: CmsSiteChromeTarget;
};

export type CmsSiteFooterModel = {
  brandLabel: string;
  copyright?: string;
  description?: string;
  editTargets?: {
    copyright?: CmsSiteChromeEditTargetAttributes;
    group?: (index: number) => CmsSiteChromeEditTargetAttributes;
    groupLink?: (
      groupIndex: number,
      linkIndex: number
    ) => CmsSiteChromeEditTargetAttributes;
    legalLink?: (index: number) => CmsSiteChromeEditTargetAttributes;
    socialLink?: (index: number) => CmsSiteChromeEditTargetAttributes;
  };
  footerExtras?: {
    externalBadgesLocale?: string;
    friendLinks: CmsSiteFooterLink[];
  };
  groups: Array<{
    label: string;
    links: CmsSiteFooterLink[];
  }>;
  legalLinks: CmsSiteFooterLink[];
  localeSwitcherModel: CmsFooterLocaleSwitcherModel;
  socialLinks: Array<{
    icon?: CmsSiteFooterIcon;
    label: string;
    target: CmsSiteChromeTarget;
  }>;
  summary?: string;
};

function renderFooterIcon(icon: CmsSiteFooterIcon | undefined) {
  if (!icon) {
    return null;
  }

  if (icon.kind === 'app') {
    return (
      <AppIconByName
        aria-hidden="true"
        className={footerStyles.socialIcon}
        name={icon.name}
      />
    );
  }

  if (icon.kind === 'discord') {
    return <DiscordIcon className={footerStyles.socialIcon} />;
  }

  if (icon.kind === 'email') {
    return renderAppIcon({ name: 'mail', className: footerStyles.socialIcon });
  }

  return (
    <CmsEditorialIcon className={footerStyles.socialIcon} name={icon.name} />
  );
}

function FooterLinkList({
  editTarget,
  links,
}: {
  editTarget?: (index: number) => CmsSiteChromeEditTargetAttributes;
  links: CmsSiteFooterLink[];
}) {
  return (
    <ul className={footerStyles.columnLinks}>
      {links.map((link, index) => (
        <li key={`${link.label}:${index}`}>
          <CmsSiteChromeTargetTrigger
            className={footerStyles.columnLink}
            dataAttributes={editTarget?.(index)}
            target={link.target}
          >
            {link.label}
            {link.badge ? <CmsNavLinkBadge label={link.badge} /> : null}
          </CmsSiteChromeTargetTrigger>
        </li>
      ))}
    </ul>
  );
}

export function CmsSiteFooterView({ model }: { model: CmsSiteFooterModel }) {
  return (
    <footer
      className={`cms-width-guard ${footerStyles.footer}`}
      data-site-runtime-chrome="footer"
    >
      <div className="cms-section cms-width-guard">
        <div className={`cms-width-guard ${footerStyles.shell}`}>
          <div className={`cms-width-guard ${footerStyles.columns}`}>
            {model.groups.map((group, index) => (
              <section
                className={footerStyles.column}
                key={`${group.label}:${index}`}
              >
                <p
                  className={footerStyles.columnTitle}
                  {...model.editTargets?.group?.(index)}
                >
                  {group.label}
                </p>
                <FooterLinkList
                  editTarget={(linkIndex) =>
                    model.editTargets?.groupLink?.(index, linkIndex) ?? {}
                  }
                  links={group.links}
                />
              </section>
            ))}
          </div>

          <div className={`cms-width-guard ${footerStyles.bottomPanel}`}>
            <div className={`cms-width-guard ${footerStyles.brandStack}`}>
              <div className={footerStyles.brandRow}>
                <div className={footerStyles.brandTitle}>
                  <Logo className={footerStyles.brandLogo} />
                  <span className={footerStyles.brandTextWrap}>
                    <span className={footerStyles.brandText}>
                      {model.brandLabel}
                    </span>
                    <span className={footerStyles.brandSuffix}>.app</span>
                  </span>
                </div>

                <CmsFooterLocaleSwitcher model={model.localeSwitcherModel} />
              </div>

              {model.summary ? (
                <p className={footerStyles.summary}>{model.summary}</p>
              ) : null}
              {model.description ? (
                <p className={footerStyles.description}>{model.description}</p>
              ) : null}
            </div>

            {model.socialLinks.length > 0 ? (
              <div className={footerStyles.socialLinks}>
                {model.socialLinks.map((link, index) => (
                  <CmsSiteChromeTargetTrigger
                    ariaLabel={link.label}
                    className={footerStyles.socialLink}
                    dataAttributes={model.editTargets?.socialLink?.(index)}
                    key={`${link.label}:${index}`}
                    target={link.target}
                  >
                    {renderFooterIcon(link.icon)}
                    <span className="sr-only">{link.label}</span>
                  </CmsSiteChromeTargetTrigger>
                ))}
              </div>
            ) : null}

            {model.footerExtras?.externalBadgesLocale ? (
              <div className={footerStyles.externalBadgesSection}>
                <FooterExternalBadges
                  locale={model.footerExtras.externalBadgesLocale}
                />
                {model.footerExtras.friendLinks.length > 0 ? (
                  <div className={footerStyles.friendLinksSection}>
                    <div className={footerStyles.friendLinksList}>
                      {model.footerExtras.friendLinks.map((link, index) => (
                        <CmsSiteChromeTargetTrigger
                          className={footerStyles.friendLink}
                          key={`${link.label}:${index}`}
                          target={link.target}
                        >
                          {link.label}
                        </CmsSiteChromeTargetTrigger>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {model.legalLinks.length > 0 ? (
              <div className={footerStyles.friendLinksSection}>
                <div className={footerStyles.friendLinksList}>
                  {model.legalLinks.map((link, index) => (
                    <CmsSiteChromeTargetTrigger
                      className={footerStyles.friendLink}
                      dataAttributes={model.editTargets?.legalLink?.(index)}
                      key={`${link.label}:${index}`}
                      target={link.target}
                    >
                      {link.label}
                    </CmsSiteChromeTargetTrigger>
                  ))}
                </div>
              </div>
            ) : null}

            {model.copyright ? (
              <div className={footerStyles.metaStack}>
                <p
                  className={footerStyles.metaCopy}
                  {...model.editTargets?.copyright}
                >
                  {model.copyright}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </footer>
  );
}
